import upperFirst from "lodash/upperFirst";
import {format} from "date-fns";
import PaymentMethod, { PaymentMethodType } from "./PaymentMethod";

export enum CardPaymentMethodStatus {
  Valid = "Valid",
  Expired = "Expired",
}

/**
 * 📄 Model representing the card payment method the user uses to pay the subscription
 */
export default class CardPaymentMethod implements PaymentMethod {
  public brand: string;
  public last4: string;
  public expMonth: number;
  public expYear: number;
  public method: PaymentMethodType;
  public country: string;

  constructor(rawPaymentMethod: any) {
    this.brand = upperFirst(rawPaymentMethod.brand);
    this.last4 = rawPaymentMethod.last4;
    this.expMonth = rawPaymentMethod.expMonth;
    this.expYear = rawPaymentMethod.expYear;
    this.method = rawPaymentMethod.method;
    this.country = rawPaymentMethod.country;
  }

  // 🚩 Flag for whether the current card is valid
  public get isActive(): boolean {
    return this.status == CardPaymentMethodStatus.Valid;
  }

  // 📦 Expiration date of the card
  public get expirationDate(): string {
    return format(new Date(this.expYear, this.expMonth, 1), "MMMM yyyy");
  }

  // 📦 Display text that will be shown in the current payment method view
  public get text(): string {
    return this.brand + " ending on " + this.last4;
  }

  // 📦 Image of the current payment method
  public get image(): string {
    switch (this.brand) {
      case "Visa":
        return "https://js.stripe.com/v3/fingerprinted/img/visa-365725566f9578a9589553aa9296d178.svg";
      case "Mastercard":
        return "https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg";
      case "Amex":
        return "https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg";
      default:
        return "";
    }
  }

  // 📦 Status of the current payment method (Valid / Expired)
  public get status(): CardPaymentMethodStatus {
    let currentDate = new Date();

    if (this.expYear < currentDate.getFullYear()) {
      return CardPaymentMethodStatus.Expired;
    }

    if (
      this.expYear == currentDate.getFullYear() &&
      this.expMonth < currentDate.getMonth()
    ) {
      return CardPaymentMethodStatus.Expired;
    }

    return CardPaymentMethodStatus.Valid;
  }

  // 🤡 Example value of the model
  public static example = new CardPaymentMethod({
    brand: "visa",
    text: "Visa ending in 4242",
    expMonth: 9,
    expYear: 2020,
  });
}
